import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { api } from './api'

const AuthContext = createContext(null)

const USER_KEY = 'lyannaj_user'

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem(USER_KEY)
      return saved ? JSON.parse(saved) : null
    } catch { return null }
  })
  const [loading, setLoading] = useState(true)
  // Ref pour distinguer un vrai login d'une restauration de session
  const freshLoginRef = useRef(false)

  // Rafraichir le profil depuis le backend (cookie httpOnly envoye automatiquement)
  useEffect(() => {
    api.auth.getMe()
      .then(freshUser => {
        setUser(freshUser)
        localStorage.setItem(USER_KEY, JSON.stringify(freshUser))
      })
      .catch(() => {
        // Cookie absent ou expire → pas de session
        setUser(null)
        localStorage.removeItem(USER_KEY)
      })
      .finally(() => setLoading(false))
  }, [])

  // Connexion email/mot de passe
  const login = useCallback(async (email, password) => {
    const response = await api.auth.login(email, password)
    const { user: newUser } = response

    freshLoginRef.current = true
    setUser(newUser)
    localStorage.setItem(USER_KEY, JSON.stringify(newUser))

    return { user: newUser }
  }, [])

  // Demander un code OTP
  const requestOtp = useCallback(async (email, nom) => {
    const response = await api.auth.requestOtp(email, nom)
    return response
  }, [])

  // Verifier le code OTP et se connecter
  const verifyOtp = useCallback(async (email, code, nom) => {
    const response = await api.auth.verifyOtp(email, code, nom)
    const { user: newUser, isNewUser } = response

    freshLoginRef.current = true
    setUser(newUser)
    localStorage.setItem(USER_KEY, JSON.stringify(newUser))

    return { user: newUser, isNewUser }
  }, [])

  // Connexion OAuth
  const loginOAuth = useCallback(async (provider, idToken, nonce) => {
    const response = await api.auth.oauth(provider, idToken, nonce)
    const { user: newUser, isNewUser } = response

    freshLoginRef.current = true
    setUser(newUser)
    localStorage.setItem(USER_KEY, JSON.stringify(newUser))

    return { user: newUser, isNewUser }
  }, [])

  // Connexion par téléphone + PIN
  const loginPin = useCallback(async (telephone, pin) => {
    const response = await api.auth.loginPin(telephone, pin)
    const { user: newUser } = response

    freshLoginRef.current = true
    setUser(newUser)
    localStorage.setItem(USER_KEY, JSON.stringify(newUser))

    return { user: newUser }
  }, [])

  // Inscription par téléphone + PIN
  const registerPin = useCallback(async (telephone, pin, prenom, nom) => {
    const response = await api.auth.registerPin(telephone, pin, prenom, nom)
    const { user: newUser } = response

    freshLoginRef.current = true
    setUser(newUser)
    localStorage.setItem(USER_KEY, JSON.stringify(newUser))

    return { user: newUser, isNewUser: true }
  }, [])

  // Deconnexion — appel backend pour supprimer le cookie httpOnly
  const logout = useCallback(async () => {
    try { await api.auth.logout() } catch { /* ignore */ }
    setUser(null)
    localStorage.removeItem(USER_KEY)
    // Vider les favoris (panier de visite + produits) au logout
    localStorage.removeItem('lyannaj_basket')
    localStorage.removeItem('lyannaj_product_favs')
    // Nettoyer l'ancien token localStorage si present (migration)
    localStorage.removeItem('lyannaj_token')
  }, [])

  // Mise a jour du profil
  const updateProfile = useCallback(async (data) => {
    const updatedUser = await api.auth.updateProfile(data)
    setUser(updatedUser)
    localStorage.setItem(USER_KEY, JSON.stringify(updatedUser))
    return updatedUser
  }, [])

  // token garde pour retrocompat des composants (truthy quand connecte)
  // Le vrai JWT est dans le cookie httpOnly, inaccessible au JS
  const token = user ? 'httponly-cookie' : null

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    freshLoginRef,
    login,
    requestOtp,
    verifyOtp,
    loginOAuth,
    loginPin,
    registerPin,
    logout,
    updateProfile,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth doit etre utilise dans un AuthProvider')
  }
  return context
}
